import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import storage from "../utils/storage";
import { axios } from "../lib/axios";

const AuthContext = createContext();

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuthContext must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  // The signed-in account (its role decides which owner-only actions are offered). The server enforces it too.
  const loadUser = useCallback(async () => {
    try {
      const response = await axios.get("/user");
      setUser(response.data && typeof response.data === "object" ? response.data : null);
    } catch (e) {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    const token = storage.getToken();
    setIsAuthenticated(!!token);
    setIsLoading(false);
    if (token) loadUser();
  }, [loadUser]);

  // Stable identities: pages list checkAuth in effect deps, so a new function
  // on every render would refetch their data whenever the provider re-rendered.
  const login = useCallback(
    (token, redirectTo) => {
      storage.setToken(token);
      setIsAuthenticated(true);
      loadUser();
      navigate(redirectTo || "/home");
    },
    [navigate, loadUser]
  );

  const logout = useCallback(() => {
    storage.clearToken();
    setIsAuthenticated(false);
    setUser(null);
    navigate("/login");
  }, [navigate]);

  const checkAuth = useCallback(() => {
    const token = storage.getToken();
    if (!token) {
      setIsAuthenticated(false);
      navigate("/login");
      return false;
    }
    return true;
  }, [navigate]);

  const value = useMemo(
    () => ({
      isAuthenticated,
      isLoading,
      login,
      logout,
      checkAuth,
      user,
      isOwner: user?.role === "owner",
    }),
    [isAuthenticated, isLoading, login, logout, checkAuth, user]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
